/**
 * Validation Type Definitions
 *
 * Defines validation result types, error messages, and validation rules
 * for configuration inputs and calculated outputs.
 */

/**
 * Validation severity levels
 */
export type ValidationSeverity = 'error' | 'warning' | 'info';

/**
 * Validation rule type
 */
export type ValidationRuleType =
  | 'required'
  | 'min'
  | 'max'
  | 'range'
  | 'pattern'
  | 'custom'
  | 'dependency'
  | 'constraint';

/**
 * Individual validation message
 */
export interface ValidationMessage {
  /** Message severity */
  severity: ValidationSeverity;

  /** Field path (e.g., "scaling.totalGPUs") */
  field: string;

  /** Error/warning code */
  code: string;

  /** Human-readable message */
  message: string;

  /** Detailed explanation (optional) */
  details?: string;

  /** Suggested fix (optional) */
  suggestion?: string;

  /** Rule that triggered this message */
  rule?: ValidationRuleType;

  /** Current value */
  currentValue?: unknown;

  /** Expected value or constraint */
  expectedValue?: unknown;
}

/**
 * Field validation result
 */
export interface FieldValidation {
  /** Field path */
  field: string;

  /** Whether field is valid */
  valid: boolean;

  /** Validation messages for this field */
  messages: readonly ValidationMessage[];

  /** Whether field has errors */
  hasErrors: boolean;

  /** Whether field has warnings */
  hasWarnings: boolean;
}

/**
 * Complete validation result
 */
export interface ValidationResult {
  /** Whether entire validation passed */
  valid: boolean;

  /** Timestamp of validation */
  timestamp: Date;

  /** Field-level validations */
  fields: Record<string, FieldValidation>;

  /** All validation messages (flattened) */
  messages: readonly ValidationMessage[];

  /** Error count */
  errorCount: number;

  /** Warning count */
  warningCount: number;

  /** Info count */
  infoCount: number;

  /** Global validation messages (not tied to specific field) */
  globalMessages: readonly ValidationMessage[];
}

/**
 * Validation rule definition
 */
export interface ValidationRule {
  /** Rule identifier */
  id: string;

  /** Rule type */
  type: ValidationRuleType;

  /** Field(s) this rule applies to */
  fields: readonly string[];

  /** Rule description */
  description: string;

  /** Validation function */
  validate: (value: unknown, context?: ValidationContext) => ValidationMessage | null;

  /** Whether rule is enabled */
  enabled: boolean;

  /** Rule priority (higher = checked first) */
  priority?: number;
}

/**
 * Validation context (additional data available during validation)
 */
export interface ValidationContext {
  /** Complete configuration object */
  configuration: unknown;

  /** Field values map */
  values: Record<string, unknown>;

  /** Previously validated fields */
  validatedFields: Set<string>;

  /** Custom context data */
  custom?: Record<string, unknown>;
}

/**
 * Validation constraints for numeric fields
 */
export interface NumericConstraints {
  /** Minimum value (inclusive) */
  min?: number;

  /** Maximum value (inclusive) */
  max?: number;

  /** Must be integer */
  integer?: boolean;

  /** Must be positive */
  positive?: boolean;

  /** Must be non-negative (>= 0) */
  nonNegative?: boolean;

  /** Must be multiple of */
  multipleOf?: number;

  /** Allowed range */
  range?: {
    min: number;
    max: number;
  };
}

/**
 * Validation constraints for string fields
 */
export interface StringConstraints {
  /** Minimum length */
  minLength?: number;

  /** Maximum length */
  maxLength?: number;

  /** Pattern (regex) */
  pattern?: RegExp;

  /** Allowed values (enum) */
  enum?: readonly string[];

  /** Must be non-empty */
  nonEmpty?: boolean;
}

/**
 * Validation constraints for arrays
 */
export interface ArrayConstraints {
  /** Minimum length */
  minLength?: number;

  /** Maximum length */
  maxLength?: number;

  /** Must be non-empty */
  nonEmpty?: boolean;

  /** Must contain unique items */
  unique?: boolean;

  /** Element type validator */
  elementType?: ValidationRule;
}

/**
 * Cross-field validation constraints
 */
export interface CrossFieldConstraint {
  /** Constraint identifier */
  id: string;

  /** Description */
  description: string;

  /** Fields involved in constraint */
  fields: readonly string[];

  /** Validation function */
  validate: (values: Record<string, unknown>) => ValidationMessage | null;
}

/**
 * Physical constraint validations
 */
export interface PhysicalConstraints {
  /** Maximum GPUs per node based on platform */
  maxGPUsPerNode?: number;

  /** Maximum nodes per rack based on RU */
  maxNodesPerRack?: number;

  /** Maximum power per rack in kW */
  maxPowerPerRackKw?: number;

  /** Maximum rack count for facility */
  maxRackCount?: number;

  /** Port count must not exceed switch capacity */
  checkPortCapacity?: boolean;

  /** Ensure network topology is feasible */
  checkTopologyFeasibility?: boolean;
}

/**
 * Network validation constraints
 */
export interface NetworkConstraints {
  /** Minimum oversubscription ratio */
  minOversubscription?: number;

  /** Maximum oversubscription ratio */
  maxOversubscription?: number;

  /** Ensure non-blocking network */
  requireNonBlocking?: boolean;

  /** Maximum network diameter (hop count) */
  maxNetworkDiameter?: number;

  /** Minimum bisection bandwidth */
  minBisectionBandwidth?: number;

  /** Check cable length feasibility */
  checkCableLengths?: boolean;
}

/**
 * Cost validation constraints
 */
export interface CostConstraints {
  /** Maximum total cost */
  maxTotalCost?: number;

  /** Maximum cost per GPU */
  maxCostPerGPU?: number;

  /** Warn if cost exceeds threshold */
  costWarningThreshold?: number;

  /** Check if all items have costs */
  requireAllCosts?: boolean;
}

/**
 * Complete validation configuration
 */
export interface ValidationConfig {
  /** Enabled validation rules */
  rules: readonly ValidationRule[];

  /** Cross-field constraints */
  crossFieldConstraints: readonly CrossFieldConstraint[];

  /** Physical constraints */
  physicalConstraints: PhysicalConstraints;

  /** Network constraints */
  networkConstraints: NetworkConstraints;

  /** Cost constraints */
  costConstraints?: CostConstraints;

  /** Whether to stop on first error */
  stopOnError: boolean;

  /** Whether to include warnings */
  includeWarnings: boolean;

  /** Whether to include info messages */
  includeInfo: boolean;
}

/**
 * Validation error types (for common errors)
 */
export const ValidationErrors = {
  REQUIRED_FIELD: 'required_field',
  INVALID_TYPE: 'invalid_type',
  OUT_OF_RANGE: 'out_of_range',
  INVALID_FORMAT: 'invalid_format',
  CONSTRAINT_VIOLATION: 'constraint_violation',
  DEPENDENCY_ERROR: 'dependency_error',
  PHYSICAL_IMPOSSIBILITY: 'physical_impossibility',
  NETWORK_INFEASIBLE: 'network_infeasible',
  INSUFFICIENT_CAPACITY: 'insufficient_capacity',
  OVERSUBSCRIBED: 'oversubscribed',
  COST_EXCEEDED: 'cost_exceeded',
} as const;

/**
 * Validation warning types (for common warnings)
 */
export const ValidationWarnings = {
  SUBOPTIMAL_CONFIG: 'suboptimal_config',
  HIGH_OVERSUBSCRIPTION: 'high_oversubscription',
  LOW_UTILIZATION: 'low_utilization',
  DEPRECATED_COMPONENT: 'deprecated_component',
  MISSING_COST: 'missing_cost',
  POWER_CONCERN: 'power_concern',
  THERMAL_CONCERN: 'thermal_concern',
  UNUSUAL_VALUE: 'unusual_value',
} as const;

/**
 * Simple validation error (for import/export operations)
 */
export interface ValidationError {
  /** Error message */
  message: string;
  /** Field name (optional) */
  field?: string;
  /** Row number (optional, for CSV imports) */
  row?: number;
  /** Error severity */
  severity?: 'error' | 'critical' | 'warning';
}

/**
 * Simple validation warning (for import/export operations)
 */
export interface ValidationWarning {
  /** Warning message */
  message: string;
  /** Field name (optional) */
  field?: string;
  /** Row number (optional, for CSV imports) */
  row?: number;
  /** Suggested action (optional) */
  suggestion?: string;
}
